import ClientModel from '@/components/ClientModel'
import { CustomPagination } from '@/components/CustomPagination'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay'
import { useDebounce } from '@/lib/useDebounce'
import {
  Client,
  useDeleteClientMutation,
  useGetClientsQuery,
} from '@/redux/service/client'
import {
  Edit,
  Archive,
  Search,
  ExternalLink,
  // ArrowUpDown,
  // ArrowUp,
  // ArrowDown,
} from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'

const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Client Name' },
  { key: 'projects', label: 'Projects' },
]

const Clients = () => {
  const [page, setPage] = useState<number>(1)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [limit, setLimit] = useState<number>(100)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const debouncedSearch = useDebounce(searchQuery, 500)
  const [editData, setEditData] = useState<Client | null>(null)
  const [isModelOpen, setIsModelOpen] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [columns, setColumns] = useState<{ key: string; label: string }[]>(
    () => {
      const savedOrder = localStorage.getItem('client-table-column-order')
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder)
          return parsedOrder
            .map((key: string) => DEFAULT_COLUMNS.find(c => c.key === key))
            .filter(Boolean) as { key: string; label: string }[]
        } catch {
          // Fallback
        }
      }
      return DEFAULT_COLUMNS
    }
  )

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const savedVisibility = localStorage.getItem('client-table-columns')
    if (savedVisibility) {
      try {
        return JSON.parse(savedVisibility)
      } catch {
        // Fallback
      }
    }
    return DEFAULT_COLUMNS.map(col => col.key)
  })

  useEffect(() => {
    localStorage.setItem(
      'client-table-column-order',
      JSON.stringify(columns.map(c => c.key))
    )
  }, [columns])

  useEffect(() => {
    localStorage.setItem(
      'client-table-columns',
      JSON.stringify(selectedColumns)
    )
  }, [selectedColumns])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedColumns.length > 1)
        : [...prev, key]
    )
  }

  const isColumnVisible = (key: string) => selectedColumns.includes(key)

  const visibleColumnsCount = useMemo(() => {
    return columns.filter(col => selectedColumns.includes(col.key)).length + 1 // +1 for Action
  }, [columns, selectedColumns])
  // const [sortBy, setSortBy] = useState<string | undefined>()
  // const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()

  // const handleSort = (column: string) => {
  //   if (sortBy === column) {
  //     if (order === 'ASC') {
  //       setOrder('DESC')
  //     } else if (order === 'DESC') {
  //       setSortBy(undefined)
  //       setOrder(undefined)
  //     }
  //   } else {
  //     setSortBy(column)
  //     setOrder('ASC')
  //   }
  //   setPage(1)
  // }

  // const getSortIcon = (column: string) => {
  //   if (sortBy !== column)
  //     return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
  //   if (order === 'ASC')
  //     return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
  //   return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  // }
  const {
    data: currentData,
    isLoading,
    isFetching,
  } = useGetClientsQuery({
    page: page,
    limit: limit,
    search: debouncedSearch,
    // sort_by: sortBy,
    // order: order,
  })
  const totalPages = Math.ceil((currentData?.total ?? 1) / limit)
  const totalRecords = currentData?.total ?? 0
  const [deleteClient, { isLoading: isDeleting }] = useDeleteClientMutation()
  const assignedProjectIds: string[] =
    currentData?.data
      ?.flatMap(client => client.projects ?? [])
      .map(project => project.project_id) ?? []

  const handleEdit = (client: Client) => {
    setEditData(client)
    setIsModelOpen(true)
  }
  const handleDeleteClick = (id: string) => {
    setSelectedClientId(id)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedClientId) return

    try {
      await deleteClient({ id: selectedClientId }).unwrap()
      toast.success('Client deleted successfully')
      setIsDeleteOpen(false)
      setSelectedClientId(null)
    } catch {
      toast.error('Failed to delete client')
    }
  }

  let tableBodyContent
  if (isLoading || isFetching) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='h-40' />
      </TableRow>
    )
  } else if ((currentData?.data?.length || 0) === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='h-40 text-center'>
          <p className='text-muted-foreground text-sm font-medium'>
            No Clients found
          </p>
        </TableCell>
      </TableRow>
    )
  } else {
    tableBodyContent = currentData?.data?.map(client => {
      return (
        <TableRow
          key={client.client_id}
          className='group hover:bg-muted/30 transition-colors'
        >
          {columns.map(column => {
            if (!isColumnVisible(column.key)) return null
            switch (column.key) {
              case 'name':
                return (
                  <TableCell key={column.key} className='font-medium truncate'>
                    {client?.link ? (
                      <a
                        href={client?.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        className=' hover:underline transition-colors flex items-center gap-1'
                      >
                        {client?.client_name ?? '—'}
                        <ExternalLink className='h-3 w-3 opacity-60' />
                      </a>
                    ) : (
                      <span className='text-foreground'>
                        {client?.client_name ?? '—'}
                      </span>
                    )}
                  </TableCell>
                )
              case 'projects':
                return (
                  <TableCell key={column.key} className='max-w-[400px]'>
                    <div className='flex items-center gap-1 flex-nowrap overflow-hidden'>
                      {client?.projects?.length ? (
                        <>
                          {client.projects.slice(0, 3).map(el => (
                            <span
                              key={el.project_id}
                              className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium truncate bg-background border border-border text-foreground max-w-[210px]'
                              title={el.project_name}
                            >
                              {el.project_name}
                            </span>
                          ))}
                          {client.projects.length > 3 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-background border border-border text-muted-foreground whitespace-nowrap cursor-help'>
                                  +{client.projects.length - 3}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className='max-w-[300px] bg-background border-border p-3  border shadow-2xl rounded-xl'>
                                <div className='space-y-2'>
                                  <p className='text-[11px] font-semibold text-muted-foreground mb-2'>
                                    Additional Projects
                                  </p>
                                  <div className='flex flex-wrap gap-1.5'>
                                    {client.projects.slice(3).map(p => (
                                      <span
                                        key={p.project_id}
                                        className='inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-background border border-border text-foreground'
                                      >
                                        {p.project_name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <span className='text-muted-foreground'>—</span>
                      )}
                    </div>
                  </TableCell>
                )
              default:
                return null
            }
          })}
          <TableCell className='flex gap-2'>
            <Button
              variant='outline'
              size='sm'
              className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground hover:border-border'
              onClick={() => handleEdit(client)}
            >
              <Edit className='w-4 h-4' />
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='w-11 bg-background border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30'
              onClick={() => handleDeleteClick(client.client_id)}
              disabled={isDeleting}
            >
              <Archive className='w-4 h-4' />
            </Button>
          </TableCell>
        </TableRow>
      )
    })
  }
  return (
    <TooltipProvider>
      <div className='h-full flex flex-col space-y-4 bg-background text-foreground overflow-hidden p-2'>
        <div className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between border-b border-border gap-4 p-2'>
          <h1 className='text-3xl font-bold tracking-tight'>Clients</h1>
          <div className='flex items-center gap-3'>
            <div className='relative w-full sm:w-64 shrink-0'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
              <Input
                placeholder='Search clients by name...'
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
              />
            </div>

            <ColumnVisibilityPopover
              columns={columns}
              isColumnVisible={isColumnVisible}
              onToggle={toggleColumn}
              onReset={() => {
                setColumns(DEFAULT_COLUMNS)
                setSelectedColumns(DEFAULT_COLUMNS.map(c => c.key))
              }}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            />
          </div>
        </div>

        <div className='flex-1 relative overflow-hidden border border-border rounded-sm'>
          <Table className='min-w-[800px] border-separate border-spacing-0 [&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2 [&_td]:border-b border-none'>
            <TableHeader>
              <TableRow className='border-b border-border'>
                {columns.map(column => {
                  if (!isColumnVisible(column.key)) return null
                  return (
                    <TableHead
                      key={column.key}
                      className='text-foreground font-medium'
                    >
                      {column.label}
                    </TableHead>
                  )
                })}
                <TableHead className='text-foreground font-medium'>
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{tableBodyContent}</TableBody>
          </Table>
          {(isLoading || isFetching) && (
            <TableLoadingOverlay label='Loading Clients...' />
          )}
        </div>
        {(currentData?.data?.length ?? 0) > 0 && !isLoading && !isFetching && (
          <div className='shrink-0'>
            <CustomPagination
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              title='Clients'
            />
          </div>
        )}

        <ClientModel
          data={editData}
          projects={assignedProjectIds}
          isOpen={isModelOpen}
          isClose={() => {
            setIsModelOpen(false)
            setEditData(null)
          }}
        />
        <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <DialogContent className='max-w-md p-6 bg-background border-border rounded-lg shadow-lg'>
            <DialogHeader>
              <DialogTitle className='text-lg font-semibold text-foreground'>
                Delete Client
              </DialogTitle>
            </DialogHeader>

            <div className='mt-2 text-sm text-muted-foreground'>
              Are you sure you want to delete this client? This action cannot be
              undone.
            </div>

            <DialogFooter className='mt-4 flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setIsDeleteOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>

              <Button
                type='button'
                variant='destructive'
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}

export default Clients
